import { isEqual } from "es-toolkit";
import { atom } from "jotai";
import { atomEffect } from "jotai-effect";
import { atomFamily } from "jotai/utils";
import type { EditorState } from "prosemirror-state";

import { Logger } from "@moment/logging";

import { $editorState } from "../prosemirror/editor-state.atom";
import { editorStateKeyForDocsRoute } from "../prosemirror/editorStateKey";
import { store } from "../store";

const logger = new Logger("cell-compiler");

// ============================================================================
// Types
// ============================================================================

export interface CompiledCells<T> {
	[key: string]: T;
}

/**
 * A compiler function that takes compiles all the compute cells in a ProseMirror `EditorState` that
 * have changed or been added. To do this, it takes the `EditorState` object and the current set of
 * compiled cells (the "previous results" of the compilation step), and produces a new set of
 * compiled cells.
 *
 * packages/state is type-agnostic - the actual types are defined in packages/app.
 */
export type CellCompilerFn<T> = (
	editorState: EditorState,
	previousResult: CompiledCells<T>
) => Promise<CompiledCells<T>>;

// ============================================================================
// Registry Atom
// ============================================================================

/**
 * Holds the registered cell compiler function. Wrapped in an object because if you pass a raw
 * function to Jotai it will invoke the function, like React does with its various hooks.
 *
 * This is set once at app initialization from packages/app.
 */
interface CellCompilerRegistry<T> {
	compiler: CellCompilerFn<T> | null;
}

const $cellCompiler = atom<CellCompilerRegistry<unknown>>({ compiler: null });
$cellCompiler.debugLabel = "cellCompiler";

/**
 * Register the cell compiler function.

 * Called once from packages/app during initialization.
 */
export const registerCellCompiler = <T>(compiler: CellCompilerFn<T>): void => {
	if (typeof compiler !== "function") {
		throw new Error("Cell compiler must be a function");
	}
	store.set($cellCompiler, { compiler: compiler as CellCompilerFn<unknown> });
	logger.debug("Cell compiler registered");
};

// ============================================================================
// Compiled Cells Storage
// ============================================================================

/**
 * Stores the compiled cells for a page.
 *
 * The actual type is managed by the packages/app code that registers the cell compiler.
 */
export const $compiledCells = atomFamily(
	({ documentId, pageId }: { documentId: string; pageId: string }) => {
		const initialCells: CompiledCells<unknown> = {};
		const cellsAtom = atom(initialCells);
		cellsAtom.debugLabel = `compiledCells/${documentId}/${pageId}`;
		return cellsAtom;
	},
	isEqual
);

// ============================================================================
// Flush Trigger
// ============================================================================

/**
 * When set to true, triggers immediate compilation (skipping debounce).
 *
 * The compilation effect will reset this to false after compiling.
 */
export const $flushCompilation = atomFamily(
	({ documentId, pageId }: { documentId: string; pageId: string }) => {
		const flushAtom = atom(false);
		flushAtom.debugLabel = `flushCompilation/${documentId}/${pageId}`;
		return flushAtom;
	},
	isEqual
);

// ============================================================================
// Compilation State (for serializing compilations)
// ============================================================================

interface CompilationState {
	/** True if a compilation is currently running */
	isCompiling: boolean;
	/** True if another compilation was requested while one was in progress */
	pendingCompile: boolean;
}

/**
 * Tracks compilation state to ensure only one compilation runs at a time.
 */
const $compilationState = atomFamily(
	({ documentId, pageId }: { documentId: string; pageId: string }) => {
		const stateAtom = atom({ isCompiling: false, pendingCompile: false } as CompilationState);
		stateAtom.debugLabel = `compilationState/${documentId}/${pageId}`;
		return stateAtom;
	},
	isEqual
);

// ============================================================================
// Compilation Effect
// ============================================================================

const DEFAULT_THROTTLE_MS = 2000;

/** @internal */
export const $throttleMs = atom(DEFAULT_THROTTLE_MS);

/** @internal */
export const $loopCount = atom(0);

/**
 * Effect that watches the editor state and compiles cells. Lots of things going on here:
 *
 *   - Compilation function invoked on any editor state change.
 *   - Compilation is throttled at `THROTTLE_MS`. Throttled, not debounced: if you keep typing, we
 *     will keep delaying compilation checks until you stop typing for `THROTTLE_MS`.
 *   - When $flushCompilation is set to true: compiles immediately, resets trigger
 *   - Only one compilation runs at a time; if a compile is requested while one is in progress, it
 *     will run again after the current one finishes
 *
 * Mount this effect by reading it from a component or via store.sub().
 *
 * !!! IMPORTANT CAVEATS !!! BE VERY CAREFUL DECIDING WHERE TO RUN THIS ATOM EFFECT! Example hazard:
 * If you run a cell with an infinite loop from (say) the docs list, it could hang the app any time
 * it opens, rather than whenever that doc opens.
 */
export const $compilationEffect = atomFamily(
	({ documentId, pageId }: { documentId: string; pageId: string }) => {
		const effect = atomEffect((get, set) => {
			// Sentinel value used for testing. We use this to make sure compile loop does not
			// spuriously re-run unless it has to.
			set($loopCount, get.peek($loopCount) + 1);

			const { compiler } = get($cellCompiler);
			if (!compiler) {
				logger.error("No cell compiler registered", { documentId, pageId });
				return;
			}
			const shouldFlush = get($flushCompilation({ documentId, pageId }));

			// Subscribe to editor state changes.
			const editorStateKey = editorStateKeyForDocsRoute({ documentId, pageId });
			const editorState = get($editorState(editorStateKey));

			if (!editorState) {
				logger.debug("Not compiling: waiting for editor state", { documentId, pageId });
				return;
			}

			const compile = async () => {
				// Check if already compiling.
				const state = get($compilationState({ documentId, pageId }));
				if (state.isCompiling) {
					// Mark that we need to compile again when current one finishes
					const pendingCompile = true;
					set($compilationState({ documentId, pageId }), { ...state, pendingCompile });
					logger.debug("Compilation already in progress, queued for later", {
						documentId,
						pageId,
					});
					return;
				}

				// Mark as compiling.
				const isCompiling = true;
				const pendingCompile = false;
				set($compilationState({ documentId, pageId }), { isCompiling, pendingCompile });

				try {
					// IMPORTANT: Use get.peek() to avoid subscribing. `$compiledCells` is output,
					// not input.
					const previousResult = get.peek($compiledCells({ documentId, pageId }));
					const newResult = await compiler(editorState, previousResult);

					// Only update and log if something actually changed.
					//
					// NOTE: This is an optimization, not a correctness requirement.
					if (newResult !== previousResult) {
						set($compiledCells({ documentId, pageId }), newResult);
						logger.debug("Compilation complete", { documentId, pageId });
					}
				} catch (error) {
					logger.error("Compilation failed", { documentId, pageId, error });
				} finally {
					// Check if another compile was requested while we were compiling
					const currentState = get($compilationState({ documentId, pageId }));
					if (currentState.pendingCompile) {
						// Reset state and compile again
						set($compilationState({ documentId, pageId }), {
							isCompiling: false,
							pendingCompile: false,
						});
						logger.debug("Running queued compilation", { documentId, pageId });
						void compile();
					} else {
						// Done, mark as not compiling
						set($compilationState({ documentId, pageId }), {
							isCompiling: false,
							pendingCompile: false,
						});
					}
				}
			};

			// Immediate flush requested.
			if (shouldFlush) {
				set($flushCompilation({ documentId, pageId }), false);
				void compile();
				return; // No cleanup needed for immediate compilation
			}

			// Initial page load: compile immediately.
			//
			// IMPORTANT: Use get.peek() to avoid subscribing. `$compiledCells` is output, not
			// input.
			const previousResult = get.peek($compiledCells({ documentId, pageId }));
			if (previousResult === undefined || Object.keys(previousResult).length === 0) {
				void compile();
				return;
			}

			// Throttled compilation for subsequent changes.
			const throttleMs = get($throttleMs);
			const timeoutId = setTimeout(() => {
				void compile();
			}, throttleMs);

			// Cleanup: cancel pending debounce when effect re-runs
			return () => clearTimeout(timeoutId);
		});

		effect.debugLabel = `compilationEffect/${documentId}/${pageId}`;
		return effect;
	},
	isEqual
);
